import * as React from "react";
import { ScatterGL } from "./ScatterGL";
import { WrapperGL } from "./WrapperGL";
import { CanvasGL } from "./CanvasGL";
import { GrafosSymbol, LineStyle } from "./interfaces/element";
import { getElementXId, getElementYId, getReferenceYValue, getObjectsReferenceYValue, } from "./utils";
import { ObjectGL } from "./ObjectGL";
import { DashLineGL } from "./DashLineGL";
import { hexColor } from "./helpers/colorParser";
import memoize from "memoize-one";
import { LineGL } from "./LineGL";
import { TimeChangingElement } from "./TimeChangingElement";
function logFunction(x, allValuesPositive) {
    return allValuesPositive
        ? Math.log(x)
        : Math.sign(x) * Math.log1p(Math.abs(x));
}
export default class GraphGL extends React.PureComponent {
    constructor() {
        super(...arguments);
        this.hexColor = memoize(hexColor);
        this.getXMapping = memoize((isLog, max, min) => {
            return isLog
                ? (x) => logFunction(x, this.props.allValuesPositive)
                : (x) => 2 * ((x - min) / (max - min)) - 1;
        });
        this.getYMapping = memoize((isLog, ref) => isLog
            ? (y) => logFunction(y, this.props.allValuesPositive)
            : ref
                ? (y) => y - ref
                : (y) => y);
        this.getXDomain = memoize((xScale, xMapper) => xScale.domain().map(xMapper));
        this.getYDomain = memoize((yScale, yMapper) => yScale.domain().map(yMapper));
        this.getIdsAndMappersElements = memoize((elements, data, isYLog, ignoreFileName) => elements.map((element) => {
            const idValue = getElementYId(element, ignoreFileName);
            const refYValue = getReferenceYValue(data, idValue);
            const yElementMapper = this.getYMapping(isYLog, refYValue);
            return { yElementMapper, idValue };
        }));
        this.getIdsAndMappersObjects = memoize((objects, isYLog) => objects.map((object) => {
            const data = object.coords.length > 1 && object.coords.length % 2 === 0
                ? object.coords.filter((_number, index) => index % 2 !== 0)
                : object.coords[0];
            const refYValue = getObjectsReferenceYValue(data);
            return this.getYMapping(isYLog, refYValue);
        }));
        this.getReferenceValueX = memoize(getMinMaxValues);
    }
    render() {
        const { width, height, margins, elements, data, isXLog, isYLog, xScale, yScale, y2Scale, objects, visibleValues, glCanvasRef, xValues, ignoreFileName, isHistogram = false, } = this.props;
        const minMaxValues = this.getReferenceValueX(data.data, xValues);
        const xMapper = this.getXMapping(isXLog, minMaxValues[1], minMaxValues[0]);
        const domainX = this.getXDomain(xScale, xMapper);
        const elementsIdsAndMappers = this.getIdsAndMappersElements(elements, data, isYLog, ignoreFileName);
        const objectYMappers = this.getIdsAndMappersObjects(objects, isYLog);
        const elementsWithColTime = elements.filter((element) => Boolean(element.colTime) &&
            visibleValues[getElementYId(element, ignoreFileName)]);
        return (React.createElement(React.Fragment, null, !isHistogram || objects.length > 0 ? (React.createElement(CanvasGL, { width: width, height: height, margins: margins, glCanvasRef: glCanvasRef },
            objects.map((object, i) => {
                const objectDomainY = this.getYDomain(yScale, objectYMappers[i]);
                return (React.createElement(ObjectGL, { key: `object_${i}`, grafosObject: object, xMapper: xMapper, yMapper: objectYMappers[i], domainX: domainX, domainY: objectDomainY }));
            }),
            elements.map((element, i) => {
                const color = this.hexColor(element.symbolColor);
                const IdValue = elementsIdsAndMappers[i].idValue;
                const yElementMapper = elementsIdsAndMappers[i].yElementMapper;
                const elementDomainY = element.colY
                    ? this.getYDomain(yScale, yElementMapper)
                    : this.getYDomain(y2Scale, yElementMapper);
                return (React.createElement(WrapperGL, { key: `${IdValue}${i}`, data: data.data, idValue: IdValue, xMapper: xMapper, yMapper: yElementMapper, domainX: domainX, domainY: elementDomainY, xKey: getElementXId(element, ignoreFileName) },
                    element.symbol !== GrafosSymbol.none &&
                        !Boolean(element.colTime) && (React.createElement(ScatterGL, { key: `scatter_${i}`, show: !isHistogram && visibleValues[IdValue], symbol: element.symbol, thickness: element.symbolSize * 3, color: color })),
                    element.lineWidth > 0 &&
                        (element.lineStyle === LineStyle.solid ? (React.createElement(LineGL, { key: `line_${element}`, show: !isHistogram && visibleValues[IdValue], thickness: element.lineWidth, color: color })) : (React.createElement(DashLineGL, { key: `line_${element}`, show: !isHistogram && visibleValues[IdValue], thickness: element.lineWidth, style: element.lineStyle, color: color })))));
            }),
            elementsWithColTime.map((element, i) => (React.createElement(TimeChangingElement, { key: `interpolated_${i}`, element: element, xMapper: xMapper, domainX: domainX, data: data, ignoreFileName: ignoreFileName, yElementMapper: elementsIdsAndMappers.find((item) => item.idValue === getElementYId(element, ignoreFileName)).yElementMapper, yScale: yScale, y2Scale: y2Scale, getYDomain: this.getYDomain, color: this.hexColor(element.symbolColor) }))))) : null));
    }
}
export const getMinMaxValues = (dataset, valuesIds) => {
    if (valuesIds) {
        let min = Number.POSITIVE_INFINITY;
        let max = Number.NEGATIVE_INFINITY;
        for (let valueId of valuesIds) {
            if (dataset[valueId] && dataset[valueId].length > 0) {
                dataset[valueId].forEach((val) => {
                    min = Math.min(min, val);
                    max = Math.max(max, val);
                });
            }
        }
        if (min === max) {
            const delta = min === 0 ? 0.1 : Math.abs(min * 0.1);
            min = min - delta;
            max = max + delta;
        }
        return [min, max];
    }
    return [-1, 1];
};
//# sourceMappingURL=GraphGL.js.map